<template>
  <div>
    <p class="crm-kicker">Contacts</p>
    <h1 class="crm-title">Carnet</h1>
    <p class="crm-hint">{{ totalLabel }}</p>

    <div class="crm-card">
      <input class="crm-input crm-input-lg" v-model="q" placeholder="Nom, société, e-mail, ORIAS, ville…" @input="onSearch" />
      <div class="crm-chips" style="margin-top:12px">
        <button
          v-for="f in filters"
          :key="f.id"
          type="button"
          class="crm-chip"
          :class="{ on: source === f.id }"
          @click="setSource(f.id)"
        >{{ f.label }}</button>
      </div>
    </div>

    <div v-if="loading" class="crm-hint">Chargement…</div>
    <div v-else>
      <router-link
        v-for="h in hits"
        :key="h.id"
        class="crm-list-row"
        :to="ficheLink(h.id)"
      >
        <div style="flex:1;min-width:0">
          <div class="crm-row-title">{{ display(h) }}</div>
          <div class="crm-row-meta">{{ meta(h) }}</div>
          <div v-if="flags(h).length" class="crm-row-flags">
            <span v-for="src in flags(h)" :key="src" class="crm-badge">{{ sourceLabel(src) }}</span>
          </div>
        </div>
        <div class="crm-row-side">
          <strong v-if="showCa(h)" class="crm-ca">{{ fmtEuro(h.ca_global) }}</strong>
          <span class="crm-badge">{{ h.relation || '—' }}</span>
        </div>
      </router-link>
      <div v-if="!hits.length" class="crm-card">
        <p class="crm-hint" style="margin:0">Aucun contact</p>
      </div>
    </div>

    <div v-if="lastPage > 1" class="crm-pager">
      <button type="button" class="crm-btn crm-btn-outline" :disabled="page <= 1 || loading" @click="go(page - 1)">Précédent</button>
      <span>{{ page }} / {{ lastPage }}</span>
      <button type="button" class="crm-btn crm-btn-outline" :disabled="page >= lastPage || loading" @click="go(page + 1)">Suivant</button>
    </div>
  </div>
</template>

<script>
import { displayName, displaySourceFlags, sourceLabel, withFrom } from './crmSalonUtils'

const PER_PAGE = 30

export default {
  name: 'CrmSalonCarnet',
  data() {
    const q = this.$route.query || {}
    return {
      q: q.q != null ? String(q.q) : '',
      source: q.source != null ? String(q.source) : '',
      hits: [],
      loading: false,
      timer: null,
      page: Math.max(1, Number(q.page) || 1),
      lastPage: 1,
      total: 0,
      filters: [
        { id: '', label: 'Tous' },
        { id: 'edouard', label: 'Édouard' },
        { id: 'karim', label: 'Karim' },
        { id: 'heomi', label: 'Héomi' },
        { id: 'monday', label: 'Monday' },
        { id: 'webinar', label: 'Webinaire' },
        { id: 'client', label: 'Client' }
      ]
    }
  },
  computed: {
    totalLabel() {
      if (this.loading && !this.total) return 'Contacts salon, Héomi, Édouard, Karim, Monday, webinaire'
      if (this.source) {
        const label = sourceLabel(this.source)
        return this.total
          ? this.total + ' contact' + (this.total > 1 ? 's' : '') + ' · ' + label
          : 'Aucun contact · ' + label
      }
      const from = this.total ? (this.page - 1) * PER_PAGE + 1 : 0
      const to = Math.min(this.page * PER_PAGE, this.total)
      return this.total
        ? from + '–' + to + ' sur ' + this.total
        : 'Aucun contact'
    }
  },
  watch: {
    '$route.query'(q) {
      const nextQ = q.q != null ? String(q.q) : ''
      const nextSource = q.source != null ? String(q.source) : ''
      const nextPage = Math.max(1, Number(q.page) || 1)
      if (nextQ === this.q && nextSource === this.source && nextPage === this.page) return
      this.q = nextQ
      this.source = nextSource
      this.page = nextPage
      this.fetch(false)
    }
  },
  mounted() { this.fetch(false) },
  methods: {
    display: displayName,
    sourceLabel,
    flags(h) {
      return displaySourceFlags(h)
    },
    showCa(h) {
      return !!(h && (h.utilisateur_id || this.flags(h).indexOf('client') >= 0))
    },
    meta(h) {
      return [h.societe, h.ville, h.pipeline].filter(Boolean).join(' · ') || '—'
    },
    fmtEuro(n) {
      const v = Number(n) || 0
      return v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
    },
    ficheLink(id) {
      return withFrom('/crm-salon/fiche/' + id, this.$route.fullPath)
    },
    syncRoute() {
      const query = {}
      if (this.q) query.q = this.q
      if (this.source) query.source = this.source
      if (this.page > 1) query.page = String(this.page)
      const cur = this.$route.query || {}
      const same =
        String(cur.q || '') === String(query.q || '') &&
        String(cur.source || '') === String(query.source || '') &&
        String(cur.page || '') === String(query.page || '')
      if (same) return
      this.$router.replace({ path: '/crm-salon/carnet', query }).catch(() => {})
    },
    onSearch() {
      this.page = 1
      clearTimeout(this.timer)
      this.timer = setTimeout(() => this.fetch(true), 280)
    },
    setSource(id) {
      this.source = id
      this.page = 1
      this.fetch(true)
    },
    go(p) {
      if (p < 1 || p > this.lastPage || p === this.page) return
      this.page = p
      this.fetch(true)
      window.scrollTo(0, 0)
    },
    async fetch(sync) {
      this.loading = true
      try {
        const { data } = await this.$apollo.query({
          client: 'AUTH',
          query: require('src/graphql/auth/crmSalon/crmSalonCarnet.gql'),
          variables: {
            q: this.q || null,
            source: this.source || null,
            page: this.page,
            per_page: PER_PAGE
          },
          fetchPolicy: 'network-only'
        })
        const page = data.crmSalonCarnet || {}
        this.hits = page.items || []
        this.total = Number(page.total) || 0
        this.page = Number(page.page) || 1
        this.lastPage = Number(page.last_page) || 1
        if (sync !== false) this.syncRoute()
      } catch (e) {
        this.hits = []
        this.total = 0
        this.lastPage = 1
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
